/**
 * TransactionReviewStep Component
 * Step 3 of the import wizard - review and import transactions
 *
 * Important: This component requires a confirmed account before import.
 * The backend will reject imports without confirmed_account_id (WP-BUG-13).
 */
import { useState, useCallback, useMemo } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { TransactionReviewTable } from '@/components/documents/TransactionReview';
import { ImportSummary } from '@/components/documents/ImportSummary';
import { useObservations, useImportTransactions } from '@/hooks/useDocuments';
import { ApiClientError } from '@/services/api';
import type { StagedImport, ObservationFilter } from '@/types/document';

export interface TransactionReviewStepProps {
  stagedImport: StagedImport;
  onComplete: () => void;
  onBack: () => void;
  onCancel: () => void;
}

/**
 * Check if the error is related to missing account confirmation
 */
function isAccountConfirmationError(error: unknown): boolean {
  if (error instanceof ApiClientError) {
    const detail = error.detail?.toLowerCase() ?? '';
    return (
      detail.includes('confirmed account') ||
      detail.includes('confirm account') ||
      detail.includes('account_id')
    );
  }
  return false;
}

export default function TransactionReviewStep({
  stagedImport,
  onComplete,
  onBack,
  onCancel,
}: TransactionReviewStepProps) {
  // Check if account is confirmed - this is required for import (WP-BUG-13)
  const hasConfirmedAccount = Boolean(stagedImport.confirmedAccountId);

  // Check if this is a balance-only import (no transactions, only balance info) (WP-BUG-25)
  const isBalanceOnly = stagedImport.isBalanceOnly ?? false;

  // State
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<ObservationFilter>('all');
  const [error, setError] = useState<string | null>(null);

  // Fetch observations
  const {
    data: observationsData,
    isLoading: observationsLoading,
    error: observationsError,
    refetch: refetchObservations,
  } = useObservations(stagedImport.id, { filter });

  // Import mutation
  const importMutation = useImportTransactions();

  // Calculate selected count
  const selectedCount = selectedIds.size;

  // Auto-select new transactions on initial load
  useMemo(() => {
    if (observationsData && selectedIds.size === 0) {
      const newIds = observationsData.observations
        .filter((o) => o.matchStatus === 'new')
        .map((o) => o.id);
      if (newIds.length > 0) {
        setSelectedIds(new Set(newIds));
      }
    }
  }, [observationsData, selectedIds.size]);

  // Handle select all
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked && observationsData) {
        setSelectedIds(new Set(observationsData.observations.map((o) => o.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [observationsData]
  );

  // Handle select individual
  const handleSelect = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  // Handle import
  const handleImport = useCallback(async () => {
    // Guard: Require confirmed account (WP-BUG-13)
    if (!hasConfirmedAccount) {
      setError('Please select an account before importing transactions. Go back to the Account Selection step.');
      return;
    }

    // Guard: Require at least one transaction OR be a balance-only import (WP-BUG-25)
    if (selectedCount === 0 && !isBalanceOnly) {
      setError('Please select at least one transaction to import.');
      return;
    }

    setError(null);

    try {
      const result = await importMutation.mutateAsync({
        stagedImportId: stagedImport.id,
        request: {
          observationIds: Array.from(selectedIds),
        },
      });

      // For balance-only imports, success means anchor was created
      // For regular imports, success means transactions were imported
      if (result.importedCount > 0 || (isBalanceOnly && result.anchorCreated)) {
        onComplete();
      } else if (isBalanceOnly) {
        // Balance-only but no anchor created - unusual error case
        setError('Failed to create balance anchor. Please try again.');
      } else {
        setError('No transactions were imported. Please check your selections.');
      }
    } catch (err) {
      // Handle account confirmation errors with specific guidance (WP-BUG-13)
      if (isAccountConfirmationError(err)) {
        setError(
          'You must select an account before importing transactions. Please go back to the Account Selection step to choose or create an account.'
        );
      } else if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to import transactions. Please try again.');
      }
    }
  }, [hasConfirmedAccount, selectedCount, isBalanceOnly, selectedIds, importMutation, stagedImport.id, onComplete]);

  // Handle skip all duplicates
  const handleSkipDuplicates = useCallback(() => {
    if (!observationsData) return;

    // Deselect all duplicates
    const duplicateIds = new Set(
      observationsData.observations.filter((o) => o.matchStatus === 'duplicate').map((o) => o.id)
    );

    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      duplicateIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  }, [observationsData]);

  // Handle observation update (from edit modal)
  const handleObservationUpdated = useCallback(() => {
    refetchObservations();
  }, [refetchObservations]);

  // Loading state
  if (observationsLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Loading transactions...</span>
        </div>
      </Card>
    );
  }

  // Error state
  if (observationsError || !observationsData) {
    return (
      <Card>
        <div className="text-center py-12">
          <ErrorIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Failed to Load Transactions</h3>
          <p className="mt-2 text-sm text-gray-500">
            There was a problem loading the transactions. Please try again.
          </p>
          <div className="mt-6">
            <Button variant="primary" onClick={() => refetchObservations()}>
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const { observations, summary, balanceInfo } = observationsData;
  const allSelected =
    observations.length > 0 && selectedIds.size === observations.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < observations.length;

  return (
    <div className="space-y-6">
      <Card>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isBalanceOnly ? 'Review Balance Import' : 'Review Transactions'}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {isBalanceOnly
                  ? 'This document contains balance information only. No transactions to import.'
                  : `${observations.length} transactions found. Select the ones you want to import.`}
              </p>
            </div>

            {/* Summary badges - only show for non-balance-only imports */}
            {!isBalanceOnly && (
              <div className="flex flex-wrap gap-2">
                <SummaryBadge label="New" count={summary.new} variant="success" />
                <SummaryBadge label="Duplicates" count={summary.duplicate} variant="warning" />
                {summary.needsReview > 0 && (
                  <SummaryBadge label="Needs Review" count={summary.needsReview} variant="error" />
                )}
              </div>
            )}
          </div>

          {/* Account selection required warning (WP-BUG-13) */}
          {!hasConfirmedAccount && (
            <Alert variant="warning" title="Account Selection Required">
              <p>
                You must select an account before importing transactions.
                Please go back to the Account Selection step to choose or create an account.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="mt-2 -ml-2 text-yellow-800 hover:text-yellow-900"
              >
                Go to Account Selection
              </Button>
            </Alert>
          )}

          {/* Balance-only import informational message (WP-BUG-25) */}
          {isBalanceOnly && hasConfirmedAccount && (
            <Alert variant="info" title="Balance Anchor Import">
              <p>
                This document contains only balance information with no transactions.
                Importing will create a balance anchor for the selected account.
              </p>
              {balanceInfo.closingBalance !== null && (
                <p className="mt-2">
                  <span className="font-medium">Closing Balance:</span>{' '}
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(balanceInfo.closingBalance)}
                  {balanceInfo.statementClosing !== null && stagedImport.parsedAccount.statementEnd && (
                    <span className="text-gray-600">
                      {' '}as of {stagedImport.parsedAccount.statementEnd}
                    </span>
                  )}
                </p>
              )}
            </Alert>
          )}

          {/* Error message */}
          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filter and bulk actions */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Filter dropdown */}
            <div className="flex items-center gap-2">
              <label htmlFor="filter" className="text-sm font-medium text-gray-700">
                Show:
              </label>
              <select
                id="filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value as ObservationFilter)}
                className="border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All</option>
                <option value="new">New only</option>
                <option value="duplicate">Duplicates only</option>
                <option value="needs_review">Needs review</option>
              </select>
            </div>

            {/* Bulk actions */}
            <div className="flex items-center gap-2 sm:ml-auto">
              {summary.duplicate > 0 && (
                <Button variant="ghost" size="sm" onClick={handleSkipDuplicates}>
                  Skip All Duplicates
                </Button>
              )}
            </div>
          </div>

          {/* Transaction table */}
          <TransactionReviewTable
            observations={observations}
            selectedIds={selectedIds}
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={handleSelectAll}
            onSelect={handleSelect}
            onObservationUpdated={handleObservationUpdated}
            stagedImportId={stagedImport.id}
          />

          {/* Empty state */}
          {observations.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No transactions found matching the filter.</p>
            </div>
          )}
        </div>
      </Card>

      {/* Balance summary */}
      <ImportSummary
        balanceInfo={balanceInfo}
        selectedCount={selectedCount}
        totalCount={observations.length}
      />

      {/* Actions */}
      <div className="flex justify-between">
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} disabled={importMutation.isPending}>
            Cancel
          </Button>
          <Button variant="ghost" onClick={onBack} disabled={importMutation.isPending}>
            Back
          </Button>
        </div>
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={!hasConfirmedAccount || (selectedCount === 0 && !isBalanceOnly)}
          isLoading={importMutation.isPending}
        >
          {!hasConfirmedAccount
            ? 'Select Account First'
            : isBalanceOnly
              ? 'Import Balance Anchor'
              : `Import ${selectedCount} ${selectedCount === 1 ? 'Transaction' : 'Transactions'}`}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Summary Badge Component
// ============================================================================

interface SummaryBadgeProps {
  label: string;
  count: number;
  variant: 'success' | 'warning' | 'error';
}

function SummaryBadge({ label, count, variant }: SummaryBadgeProps) {
  const colors = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[variant]}`}>
      {count} {label}
    </span>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-6 w-6 text-primary-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
